import React, { useState, useMemo, useEffect, useCallback, useRef, useLayoutEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import ContactForm from '../../Components/ContactForm';
import RentForm from '../../Components/RentForm';
import ForwardForm from '../../Components/ForwardForm';
import TablePagination from '../../Components/TablePagination';
import { 
    SearchIcon, 
    CrossIcon, 
    SparklesIcon, 
    CheckIcon, 
    LockIcon, 
    RefreshIcon, 
    ExternalLinkIcon, 
    ChevronDownIcon, 
    CopyIcon,
    TagIcon,
    BuyIcon,
    RentIcon,
    ForwardIcon,
    GlobeIcon,
    AlertCircleIcon
} from '../../Components/Icons';
import { extractHashtags, stripHashtags, getUniqueHashtags, scoreDomainRelevance, simplifySource, formatDomainPunycode, formatCompactPrice } from '../../Utils/hashtag';
import { triggerHaptic } from '../../Utils/miniApp';

export default function SearchDOMAINS() {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('');
    const location = useLocation();
    const [currentPage, setCurrentPage] = useState(1);
    const [viewMode, setViewMode] = useState(() => {
        try {
            const saved = localStorage.getItem('domainer_home_viewMode');
            return saved === 'table' ? 'table' : 'grid';
        } catch (_) {
            return 'grid';
        }
    });
    const [isMobile, setIsMobile] = useState(() => {
        return typeof window !== 'undefined' ? window.innerWidth < 768 : false;
    });
    const [domainItems, setDomainItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [activeTab, setActiveTab] = useState('contact');
    const [selectedDomain, setSelectedDomain] = useState(null);

    // Live WHOIS Registry check state
    const [whoisConfig, setWhoisConfig] = useState({
        whoisMode: 'off',
        enabled: false,
        partnerRegistratorUrl: 'https://nic.tg',
        rateLimitSeconds: 5
    });
    const [whoisLoading, setWhoisLoading] = useState(false);
    const [whoisResult, setWhoisResult] = useState(null);
    const [whoisError, setWhoisError] = useState(null);
    const [rawOpen, setRawOpen] = useState(false);
    const [copied, setCopied] = useState(false);
    const [queueElapsed, setQueueElapsed] = useState(0);
    const whoisTimerRef = useRef(null);
    const searchInputRef = useRef(null);

    const isWhoisEnabled = Boolean(whoisConfig?.enabled && whoisConfig?.whoisMode && whoisConfig.whoisMode !== 'off');

    useEffect(() => {
        return () => {
            if (whoisTimerRef.current) clearInterval(whoisTimerRef.current);
        };
    }, []);

    const setShowContactForm = (val) => {
        if (val) {
            setActiveTab('contact');
            setIsModalOpen(true);
        } else {
            setIsModalOpen(false);
        }
    };
    const setShowRentForm = (val) => {
        if (val) {
            setActiveTab('rent');
            setIsModalOpen(true);
        } else {
            setIsModalOpen(false);
        }
    };
    const setShowForwardForm = (val) => {
        if (val) {
            setActiveTab('forward');
            setIsModalOpen(true);
        } else {
            setIsModalOpen(false);
        }
    };

    // Auto-focus search input on initial page load (desktop only, to avoid pulling up mobile keyboard)
    useEffect(() => {
        if (typeof window !== 'undefined' && window.innerWidth < 768) return;
        const timer = setTimeout(() => {
            if (searchInputRef.current) {
                searchInputRef.current.focus({ preventScroll: true });
            }
        }, 120);
        return () => clearTimeout(timer);
    }, []);

    // Load whois config
    useEffect(() => {
        fetch('/api/whois/config')
            .then(res => res.json())
            .then(data => {
                if (data) {
                    setWhoisConfig(prev => ({ 
                        ...prev, 
                        ...data,
                        enabled: Boolean(data.enabled && data.whoisMode && data.whoisMode !== 'off')
                    }));
                }
            })
            .catch(() => {});
    }, []);

    // Sync search term from URL query if provided (e.g. /?q=crypto or /?tag=web3)
    useEffect(() => {
        const params = new URLSearchParams(location.search);
        const q = params.get('q') || params.get('tag');
        if (q) setSearchTerm(q);
    }, [location.search]);

    // Load domains from API with automatic background retry and skeleton fallback
    useEffect(() => {
        let isMounted = true;
        const loadDomains = async (retryCount = 0) => {
            try {
                if (retryCount === 0 && domainItems.length === 0) {
                    setLoading(true);
                }
                const response = await fetch('/api/domains');
                if (!response.ok) throw new Error('Failed to load domains');
                const data = await response.json();
                if (!isMounted) return;
                if (Array.isArray(data)) {
                    if (data.length === 0) {
                        setError('no_active_domains');
                    } else {
                        setDomainItems(data);
                        setError(null);
                    }
                }
                setLoading(false);
            } catch (err) {
                console.warn('Error loading domains (attempt ' + (retryCount + 1) + '):', err);
                if (!isMounted) return;
                // If network glitched, retry once before showing error
                if (retryCount < 1) {
                    setTimeout(() => {
                        if (isMounted) loadDomains(retryCount + 1);
                    }, 400);
                } else {
                    if (domainItems.length === 0) {
                        setError('load_error');
                    }
                    setLoading(false);
                }
            }
        };
        loadDomains();
        return () => { isMounted = false; };
    }, [location.pathname]);

    // Track screen size
    useEffect(() => {
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        window.addEventListener('resize', handleResize);
        return () => window.removeEventListener('resize', handleResize);
    }, []);

    // Save view mode
    useEffect(() => {
        localStorage.setItem('viewMode', viewMode);
    }, [viewMode]);

    // Unique hashtags collected from domain descriptions
    const availableTags = useMemo(() => {
        return getUniqueHashtags(domainItems);
    }, [domainItems]);

    // Filter and sort domains with relevance scoring and hashtag support
    const filteredAndSortedDomains = useMemo(() => {
        let filtered = domainItems;

        if (searchTerm && searchTerm.trim()) {
            const rawQ = searchTerm.trim().toLowerCase();
            const isTagSearch = rawQ.startsWith('#');
            const cleanTag = isTagSearch ? rawQ.slice(1) : rawQ;

            filtered = filtered.filter(domain => {
                const name = (domain.name || '').toLowerCase();
                const desc = (domain.description || '').toLowerCase();
                const tags = extractHashtags(domain.description);

                if (isTagSearch) {
                    return tags.includes(cleanTag) || tags.some(t => t.includes(cleanTag));
                }
                return name.includes(cleanTag) || desc.includes(cleanTag) || tags.includes(cleanTag);
            });

            // If user did not pick an explicit sort order, sort by relevance score!
            if (!sortBy) {
                filtered = [...filtered].sort((a, b) => {
                    const scoreA = scoreDomainRelevance(a, searchTerm);
                    const scoreB = scoreDomainRelevance(b, searchTerm);
                    return scoreB - scoreA;
                });
            }
        }

        // Explicit user sorting
        if (sortBy) {
            filtered = [...filtered].sort((a, b) => {
                switch (sortBy) {
                    case 'price-low':
                        return a.pricebuy - b.pricebuy;
                    case 'price-high':
                        return b.pricebuy - a.pricebuy;
                    case 'name-az':
                        return a.name.localeCompare(b.name);
                    case 'name-za':
                        return b.name.localeCompare(a.name);
                    default:
                        return 0;
                }
            });
        }

        return filtered;
    }, [domainItems, searchTerm, sortBy]);

    // Reset pagination to page 1 on filter change
    useEffect(() => {
        setCurrentPage(1);
    }, [searchTerm, sortBy]);

    // Paginate to max 100 domains per page
    const paginatedDomains = useMemo(() => {
        const startIndex = (currentPage - 1) * 100;
        return filteredAndSortedDomains.slice(startIndex, startIndex + 100);
    }, [filteredAndSortedDomains, currentPage]);

    // Check if query is eligible for external WHOIS check:
    const canSearchRegistry = useMemo(() => {
        if (!searchTerm) return false;
        const q = searchTerm.trim();
        if (q.startsWith('#')) return false;
        if (q.length < 2) return false;
        const parsed = formatDomainPunycode(q);
        return parsed.isValid;
    }, [searchTerm]);

    // Normalized query for registry lookup (only if canSearchRegistry is true)
    const normalizedTargetDomain = useMemo(() => {
        if (!canSearchRegistry) return null;
        return formatDomainPunycode(searchTerm.trim());
    }, [searchTerm, canSearchRegistry]);

    // Trigger live WHOIS check
    const handleWhoisCheck = useCallback(async (target) => {
        let domainToSearch = target;
        if (!domainToSearch && normalizedTargetDomain?.punycode) {
            domainToSearch = normalizedTargetDomain.punycode;
        } else if (!domainToSearch && searchTerm) {
            const parsed = formatDomainPunycode(searchTerm);
            if (parsed.isValid) domainToSearch = parsed.punycode;
        }

        if (!domainToSearch) return;

        if (whoisConfig.whoisMode === 'off' || !whoisConfig.enabled) {
            setWhoisError('Registry lookup is currently disabled.');
            return;
        }

        const parsed = formatDomainPunycode(domainToSearch);

        triggerHaptic('light');
        setWhoisLoading(true);
        setWhoisError(null);
        setWhoisResult(null);
        setQueueElapsed(0);
        setRawOpen(false);

        const startTime = Date.now();
        if (whoisTimerRef.current) clearInterval(whoisTimerRef.current);
        whoisTimerRef.current = setInterval(() => {
            setQueueElapsed(Math.floor((Date.now() - startTime) / 1000));
        }, 500);

        try {
            const res = await fetch('/api/whois/check', {
                method: 'POST',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ domain: parsed.punycode || domainToSearch })
            });
            const data = await res.json();
            if (!res.ok || !data.success) {
                throw new Error(data.error || 'Failed to check domain');
            }
            setWhoisResult({
                ...data,
                originalUnicode: parsed.unicode || data.domain,
                isIdn: parsed.isIdn
            });
            triggerHaptic(data.isAvailable ? 'success' : 'warning');
        } catch (err) {
            const msg = err.message && !err.message.includes('Failed to fetch') && !err.message.includes('NetworkError')
                ? err.message
                : 'Unable to connect to registry. Please check your connection and try again.';
            setWhoisError(msg);
            triggerHaptic('error');
        } finally {
            if (whoisTimerRef.current) clearInterval(whoisTimerRef.current);
            setWhoisLoading(false);
        }
    }, [normalizedTargetDomain, whoisConfig, searchTerm]);

    const handleCopyRaw = () => {
        if (!whoisResult?.whoisRaw) return;
        navigator.clipboard.writeText(whoisResult.whoisRaw).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        });
    };

    const handleTagClick = (tag) => {
        const newTerm = searchTerm === `#${tag}` ? '' : `#${tag}`;
        setSearchTerm(newTerm);
        setWhoisResult(null);
        setWhoisError(null);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 30);
    };

    const handleResetFilters = useCallback(() => {
        setSearchTerm('');
        setSortBy('');
        setWhoisResult(null);
        setWhoisError(null);
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 30);
    }, []);

    const handleCloseWhois = useCallback(() => {
        setWhoisResult(null);
        setWhoisError(null);
        setSearchTerm('');
        setSortBy('');
        setTimeout(() => {
            searchInputRef.current?.focus();
        }, 30);
    }, []);

    const isWhoisMode = Boolean(whoisLoading || whoisResult);



    // Error state - only show if loading completed and domain list is genuinely empty/unreachable
    if (error && !loading && domainItems.length === 0) {
        return (
            <section id="domains" className="py-20 px-6 md:px-8 lg:px-16">
                <div className="max-w-7xl mx-auto space-y-12">
                    <motion.div 
                        initial={{ opacity: 0, y: -15 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                        className="text-center space-y-4"
                    >
                        <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white tracking-tight">
                            Premium <span className="gradient-text">DOMAINS</span>
                        </h2>
                        <p className="text-base sm:text-lg text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                            Discover premium domain names perfect for your business or project, or verify new domain availability
                        </p>
                    </motion.div>
                    <motion.div 
                        initial={{ opacity: 0, scale: 0.98 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ duration: 0.35, delay: 0.1 }}
                        className="flex justify-center items-center py-16"
                    >
                        <div className="relative rounded-3xl p-8 sm:p-10 max-w-xl mx-auto text-center space-y-5 bg-gradient-to-b from-neutral-900/90 via-neutral-950/90 to-dark-100 border border-white/[0.12] shadow-2xl backdrop-blur-2xl">
                            <div className="w-14 h-14 bg-amber-500/10 border border-amber-500/20 text-amber-400 rounded-2xl flex items-center justify-center mx-auto shadow-lg shadow-amber-500/10">
                                <AlertCircleIcon className="w-7 h-7" />
                            </div>
                            <div className="space-y-2">
                                <h3 className="text-2xl sm:text-3xl font-display font-bold text-white tracking-tight">
                                    {error === 'no_active_domains' ? 'No Active Domains' : 'Unable to Load Domains'}
                                </h3>
                                <p className="text-neutral-400 text-sm sm:text-base leading-relaxed">
                                    {error === 'no_active_domains' 
                                        ? 'There are currently no active domains available. Please check back later.' 
                                        : 'Please check your connection and try again.'}
                                </p>
                            </div>
                            <div className="pt-2">
                                <button 
                                    onClick={() => window.location.reload()}
                                    className="btn btn-primary px-6 py-2.5 text-sm font-semibold rounded-xl"
                                >
                                    <span className="relative z-10">Try Again</span>
                                </button>
                            </div>
                        </div>
                    </motion.div>
                </div>
            </section>
        );
    }

    return (
        <section id="domains" className="py-20 px-6 md:px-8 lg:px-16 scroll-mt-20">
            <div className="max-w-7xl mx-auto space-y-8">
                {/* Section Header with smooth entrance animation like Whois page */}
                <motion.div 
                    initial={{ opacity: 0, y: -15 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.45, ease: [0.16, 1, 0.3, 1] }}
                    className="text-center space-y-4"
                >
                    <h2 className="text-3xl md:text-4xl lg:text-5xl font-display font-bold text-white tracking-tight">
                        Premium <span className="gradient-text">DOMAINS</span>
                    </h2>
                    <p className="text-base sm:text-lg text-neutral-400 max-w-2xl mx-auto leading-relaxed">
                        Discover premium domain names perfect for your business or project, or verify new domain availability
                    </p>
                </motion.div>

                {/* Search Bar & Filters with smooth entrance animation */}
                <motion.div
                    initial={{ opacity: 0, scale: 0.98, y: 10 }}
                    animate={{ opacity: 1, scale: 1, y: 0 }}
                    transition={{ duration: 0.38, delay: 0.08, ease: [0.16, 1, 0.3, 1] }}
                >
                    <SearchAndFilters 
                        inputRef={searchInputRef}
                        searchTerm={searchTerm}
                        setSearchTerm={(term) => {
                            setSearchTerm(term);
                            setWhoisResult(null);
                            setWhoisError(null);
                        }}
                        sortBy={sortBy}
                        setSortBy={setSortBy}
                        viewMode={viewMode}
                        setViewMode={(mode) => {
                            setViewMode(mode);
                            try {
                                localStorage.setItem('domainer_home_viewMode', mode);
                                localStorage.setItem('viewMode', mode);
                            } catch (_) {}
                        }}
                        isMobile={isMobile}
                        resultsCount={filteredAndSortedDomains.length}
                        availableTags={availableTags}
                        onTagClick={handleTagClick}
                        onTriggerWhois={handleWhoisCheck}
                        whoisConfig={whoisConfig}
                        isWhoisEnabled={isWhoisEnabled}
                        canSearchRegistry={canSearchRegistry}
                        normalizedTargetDomain={normalizedTargetDomain}
                        whoisLoading={whoisLoading}
                        isWhoisMode={isWhoisMode}
                        onReset={handleResetFilters}
                    />
                </motion.div>

                {/* Live Content Slot: Skeleton while loading, or AnimatePresence between WHOIS and Catalog */}
                {loading ? (
                    <motion.div
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ duration: 0.2 }}
                    >
                        <DomainSkeleton viewMode={viewMode} />
                    </motion.div>
                ) : (
                    <AnimatePresence exitBeforeEnter mode="wait">
                    {whoisLoading ? (
                        <motion.div
                            key="whois-loading"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.18 }}
                        >
                            <WhoisSkeletonCard 
                                queueElapsed={queueElapsed} 
                                targetDomain={normalizedTargetDomain?.unicode || searchTerm} 
                            />
                        </motion.div>
                    ) : whoisError ? (
                        <motion.div
                            key="whois-error"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.2 }}
                            className="max-w-2xl mx-auto p-4 rounded-xl bg-red-500/10 border border-red-500/30 text-red-300 text-sm flex items-center justify-between gap-3 mb-8"
                        >
                            <div className="flex items-center gap-3">
                                <CrossIcon className="w-5 h-5 flex-shrink-0 text-red-400" />
                                <span>{whoisError}</span>
                            </div>
                            <button
                                type="button"
                                onClick={handleCloseWhois}
                                className="p-1.5 text-red-400 hover:text-white rounded-lg hover:bg-white/10 transition-colors cursor-pointer"
                                title="Close"
                            >
                                <CrossIcon className="w-4 h-4" />
                            </button>
                        </motion.div>
                    ) : whoisResult ? (
                        <motion.div
                            key="whois-result"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.25 }}
                            className="max-w-3xl mx-auto mb-12"
                        >
                            {whoisResult.isAvailable ? (
                                /* ================= AVAILABLE DOMAIN CARD ================= */
                                <div className="relative overflow-hidden rounded-3xl bg-gradient-to-br from-emerald-950/40 via-dark-100 to-dark-200 border-2 border-emerald-500/40 shadow-2xl p-6 sm:p-8">
                                    <div className="absolute -right-20 -top-20 w-64 h-64 bg-emerald-500/15 rounded-full blur-3xl pointer-events-none" />

                                    {/* Close WHOIS result button (icon only, stays on current page) */}
                                    <button
                                        type="button"
                                        onClick={handleCloseWhois}
                                        className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-neutral-400 hover:text-white border border-white/10 hover:border-white/30 flex items-center justify-center transition-all shadow-sm z-20 cursor-pointer active:scale-95"
                                        title="Close"
                                        aria-label="Close"
                                    >
                                        <CrossIcon className="w-3.5 h-3.5" />
                                    </button>

                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-6 relative z-10 pr-12 sm:pr-14">
                                        <div>
                                            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/30 text-emerald-400 text-xs font-bold uppercase tracking-wider mb-3">
                                                <CheckIcon className="w-4 h-4" />
                                                <span>Available for Registration</span>
                                            </div>

                                            <h2 className="text-3xl sm:text-4xl font-extrabold text-white font-display tracking-tight mb-2 flex flex-wrap items-baseline gap-2">
                                                <span>{whoisResult.originalUnicode || whoisResult.domain}</span>
                                                {whoisResult.isIdn && (
                                                    <span className="text-sm sm:text-base font-normal font-mono text-emerald-300/70">
                                                        ({whoisResult.domain})
                                                    </span>
                                                )}
                                            </h2>

                                            <p className="text-emerald-300/90 text-sm sm:text-base font-medium max-w-md flex items-center gap-1.5">
                                                <SparklesIcon className="w-5 h-5 text-emerald-400 shrink-0" />
                                                <span>Great news! You can register this domain right now directly with the official registrar.</span>
                                            </p>

                                            {whoisConfig.whoisMode === 'full' && whoisResult.checkedMethod && (
                                                <div className="flex items-center gap-3 mt-4 text-xs text-neutral-400 font-mono">
                                                    <span>Source: {simplifySource(whoisResult.checkedMethod)}</span>
                                                </div>
                                            )}
                                        </div>

                                        <div className="flex flex-col gap-3 sm:items-end shrink-0">
                                            <a
                                                href={whoisResult.partnerRegistratorUrl || 'https://nic.tg'}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={() => triggerHaptic('impact')}
                                                className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl bg-gradient-to-r from-emerald-500 to-teal-500 hover:from-emerald-600 hover:to-teal-600 text-white font-bold text-sm sm:text-base shadow-xl shadow-emerald-500/25 hover:shadow-emerald-500/40 transition-all active:scale-95 text-center"
                                            >
                                                <span>Register Domain Now</span>
                                                <ExternalLinkIcon className="w-4 h-4" />
                                            </a>

                                            <span className="text-[11px] text-neutral-400 text-center sm:text-right">
                                                Official Registrar Partner link
                                            </span>
                                        </div>
                                    </div>
                                </div>
                            ) : (
                                /* ================= REGISTERED DOMAIN CARD ================= */
                                <div className="relative rounded-3xl bg-dark-100/90 border border-white/10 shadow-2xl p-6 sm:p-8 backdrop-blur-xl">
                                    {/* Close WHOIS result button (icon only, stays on current page) */}
                                    <button
                                        type="button"
                                        onClick={handleCloseWhois}
                                        className="absolute top-4 right-4 sm:top-5 sm:right-5 w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-neutral-400 hover:text-white border border-white/10 hover:border-white/30 flex items-center justify-center transition-all shadow-sm z-20 cursor-pointer active:scale-95"
                                        title="Close"
                                        aria-label="Close"
                                    >
                                        <CrossIcon className="w-3.5 h-3.5" />
                                    </button>

                                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-white/5 pr-12 sm:pr-14">
                                        <div>
                                            <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-amber-300 text-xs font-semibold mb-2">
                                                <LockIcon className="w-3.5 h-3.5" />
                                                <span>Domain Already Registered</span>
                                            </div>
                                            <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display flex flex-wrap items-baseline gap-2">
                                                <span>{whoisResult.originalUnicode || whoisResult.domain}</span>
                                                {whoisResult.isIdn && (
                                                    <span className="text-sm sm:text-base font-normal font-mono text-neutral-400">
                                                        ({whoisResult.domain})
                                                    </span>
                                                )}
                                            </h2>
                                        </div>

                                        {whoisConfig.whoisMode === 'full' && (
                                            <div className="text-xs text-neutral-400 font-mono text-left sm:text-right space-y-0.5">
                                                {whoisResult.parsedDetails?.status && (
                                                    <div>Status: <span className="text-white font-semibold">{whoisResult.parsedDetails.status}</span></div>
                                                )}
                                                {whoisResult.checkedMethod && (
                                                    <div>Source: <span className="text-white font-semibold">{simplifySource(whoisResult.checkedMethod)}</span></div>
                                                )}
                                            </div>
                                        )}
                                    </div>

                                    {/* Structured Metadata Grid */}
                                    <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
                                        <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5">
                                            <div className="text-[11px] text-neutral-400 uppercase tracking-wider mb-1 font-semibold">Registrar</div>
                                            <div className="text-white font-medium text-sm truncate">{whoisResult.parsedDetails?.registrar || 'N/A'}</div>
                                        </div>

                                        <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5">
                                            <div className="text-[11px] text-neutral-400 uppercase tracking-wider mb-1 font-semibold">Registration Date</div>
                                            <div className="text-white font-medium text-sm">{whoisResult.parsedDetails?.activationDate || 'N/A'}</div>
                                        </div>

                                        <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5">
                                            <div className="text-[11px] text-neutral-400 uppercase tracking-wider mb-1 font-semibold">Expiration Date</div>
                                            <div className="text-white font-medium text-sm">{whoisResult.parsedDetails?.expirationDate || 'N/A'}</div>
                                        </div>
                                    </div>

                                    {/* Name Servers */}
                                    {whoisResult.parsedDetails?.nameServers?.length > 0 && (
                                        <div className="mb-6 p-3.5 rounded-xl bg-dark-200/50 border border-white/5">
                                            <div className="text-[11px] text-neutral-400 uppercase tracking-wider mb-2 font-semibold flex items-center gap-1.5">
                                                <GlobeIcon className="w-3.5 h-3.5 text-primary-400" />
                                                <span>Name Servers</span>
                                            </div>
                                            <div className="flex flex-wrap gap-2">
                                                {whoisResult.parsedDetails.nameServers.map((ns, idx) => (
                                                    <span key={idx} className="px-2.5 py-1 rounded-md bg-dark-300 text-xs font-mono text-neutral-300 border border-white/5">
                                                        {ns}
                                                    </span>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Collapsible Raw WHOIS Record (only if whoisMode === 'full') */}
                                    {whoisResult.whoisRaw && whoisConfig.whoisMode !== 'min' && (
                                        <div className="border-t border-white/5 pt-4">
                                            <div className="flex items-center justify-between">
                                                <button
                                                    type="button"
                                                    onClick={() => setRawOpen(prev => !prev)}
                                                    className="flex items-center gap-2 text-xs font-semibold text-neutral-300 hover:text-white transition-colors cursor-pointer"
                                                >
                                                    <ChevronDownIcon className={`w-4 h-4 transition-transform duration-200 ${rawOpen ? 'rotate-180' : ''}`} />
                                                    <span>{rawOpen ? 'Hide Full WHOIS Record' : 'Show Full WHOIS Record'}</span>
                                                </button>

                                                {rawOpen && (
                                                    <button
                                                        type="button"
                                                        onClick={handleCopyRaw}
                                                        className="flex items-center gap-1.5 text-xs text-primary-400 hover:text-primary-300 font-medium transition-colors cursor-pointer"
                                                    >
                                                        <CopyIcon className="w-3.5 h-3.5" />
                                                        <span>{copied ? 'Copied!' : 'Copy RAW'}</span>
                                                    </button>
                                                )}
                                            </div>

                                            {rawOpen && (
                                                <pre className="mt-3 p-4 rounded-xl bg-dark-300/80 border border-white/5 text-[11px] font-mono text-neutral-300 overflow-x-auto max-h-64 leading-relaxed select-all">
                                                    {whoisResult.whoisRaw}
                                                </pre>
                                            )}
                                        </div>
                                    )}
                                </div>
                            )}
                        </motion.div>
                    ) : (
                        /* ================= MARKETPLACE CATALOG VIEW ================= */
                        <motion.div
                            key="marketplace-catalog"
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            transition={{ duration: 0.18, ease: [0.16, 1, 0.3, 1] }}
                        >
                            {filteredAndSortedDomains.length === 0 ? (
                        /* Empty State: No domains matched in marketplace */
                        <div className="relative overflow-hidden rounded-2xl bg-gradient-to-b from-dark-100/90 via-dark-200/50 to-dark-100/90 border border-white/10 p-5 sm:p-6 max-w-md mx-auto text-center space-y-3.5 shadow-2xl backdrop-blur-xl my-6 animate-fadeIn">
                            <div className="absolute top-0 left-1/2 -translate-x-1/2 w-44 h-[1px] bg-gradient-to-r from-transparent via-primary-500/50 to-transparent" />

                            <div className="space-y-2">
                                <h3 className="text-base sm:text-lg font-bold text-white font-display tracking-tight flex items-center justify-center gap-2">
                                    <SearchIcon className="w-4 h-4 text-primary-400 shrink-0" />
                                    <span>No Marketplace Matches</span>
                                </h3>

                                <div className="text-xs sm:text-sm text-neutral-400 max-w-sm mx-auto leading-relaxed space-y-1">
                                    {searchTerm ? (
                                        <>
                                            <p>
                                                No premium domains in our catalog match <span className="text-primary-300 font-semibold font-mono">"{searchTerm}"</span>.
                                            </p>
                                            {isWhoisEnabled && canSearchRegistry ? (
                                                <p className="text-neutral-400">
                                                    You can check if this domain is available in the official registry.
                                                </p>
                                            ) : (
                                                <p className="text-neutral-500">
                                                    Try modifying your search query and try again.
                                                </p>
                                            )}
                                        </>
                                    ) : (
                                        <p>No domains match the selected filter criteria.</p>
                                    )}
                                </div>
                            </div>

                            {/* Direct Registry Search Button */}
                            {isWhoisEnabled && canSearchRegistry && (
                                <div className="pt-1">
                                    <button
                                        onClick={() => handleWhoisCheck(normalizedTargetDomain?.punycode || normalizedTargetDomain)}
                                        disabled={whoisLoading}
                                        className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gradient-to-r from-primary-500 to-indigo-600 hover:from-primary-600 hover:to-indigo-500 active:scale-95 text-white text-xs sm:text-sm font-semibold shadow-md shadow-primary-500/25 transition-all cursor-pointer"
                                    >
                                        {whoisLoading ? (
                                            <RefreshIcon className="w-4 h-4 animate-spin" />
                                        ) : (
                                            <SearchIcon className="w-4 h-4" />
                                        )}
                                        <span>Check "{normalizedTargetDomain?.unicode || normalizedTargetDomain}" in Registry</span>
                                    </button>
                                </div>
                            )}

                            <div className="pt-0.5">
                                <button
                                    onClick={handleResetFilters}
                                    className="inline-flex items-center gap-1.5 text-xs text-neutral-400 hover:text-white transition-colors py-1.5 px-3 rounded-lg hover:bg-white/5 cursor-pointer font-medium"
                                >
                                    <CrossIcon className="w-2.5 h-2.5 opacity-70" />
                                    <span>Reset search & show all domains</span>
                                </button>
                            </div>
                        </div>
                    ) : (
                        <>
                            <TablePagination
                                currentPage={currentPage}
                                totalItems={filteredAndSortedDomains.length}
                                pageSize={100}
                                onPageChange={setCurrentPage}
                                showMaxLabel={false}
                                position="top"
                                hideIfSinglePage={true}
                            />
                            <DomainList 
                                domains={paginatedDomains} 
                                viewMode={viewMode} 
                                isMobile={isMobile}
                                setShowContactForm={setShowContactForm}
                                setShowRentForm={setShowRentForm}
                                setShowForwardForm={setShowForwardForm}
                                setSelectedDomain={setSelectedDomain}
                                onSelectTag={handleTagClick}
                            />
                            <TablePagination
                                currentPage={currentPage}
                                totalItems={filteredAndSortedDomains.length}
                                pageSize={100}
                                onPageChange={setCurrentPage}
                                showMaxLabel={false}
                                hideIfSinglePage={true}
                            />

                            {/* Inline check suggestion banner: appears AFTER premium domains catalog */}
                            {isWhoisEnabled && canSearchRegistry && (
                                <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-3 p-3.5 rounded-2xl bg-gradient-to-r from-primary-500/10 via-dark-200/50 to-indigo-500/10 border border-primary-500/20 backdrop-blur-md mt-6 animate-fadeIn">
                                    <div className="flex items-center gap-2.5 text-xs sm:text-sm">
                                        <SparklesIcon className="w-4 h-4 text-primary-400 shrink-0" />
                                        <span className="text-neutral-300">
                                            Looking to register a new domain? Check availability of <span className="text-white font-bold font-mono">{normalizedTargetDomain?.unicode || normalizedTargetDomain}</span>
                                        </span>
                                    </div>
                                    <button
                                        onClick={() => handleWhoisCheck(normalizedTargetDomain?.punycode || normalizedTargetDomain)}
                                        className="px-4 py-1.5 rounded-xl bg-primary-500 hover:bg-primary-600 text-white text-xs font-semibold shadow-md shadow-primary-500/20 transition-all flex items-center gap-1.5 shrink-0 active:scale-95 cursor-pointer"
                                    >
                                        <SearchIcon className="w-3.5 h-3.5" />
                                        <span>Check Registry</span>
                                    </button>
                                </div>
                            )}
                        </>
                    )}
                        </motion.div>
                    )}
                </AnimatePresence>
            )}
            </div>
            
            {/* Action Modals */}
            {selectedDomain && (() => {
                const modalHasBuy = Boolean(selectedDomain.pricebuy && Number(selectedDomain.pricebuy) > 0);
                const modalHasRent = Boolean(selectedDomain.pricerent && Number(selectedDomain.pricerent) > 0);
                const modalHasForward = Boolean(selectedDomain.pricefw && Number(selectedDomain.pricefw) > 0);

                return (
                    <div 
                        className={isModalOpen ? "fixed inset-0 bg-black/85 backdrop-blur-xl z-50 flex items-center justify-center p-2.5 sm:p-6 overflow-y-auto modal-scrollbar animate-in fade-in duration-200" : "hidden"} 
                        onClick={(e) => {
                            if (e.target === e.currentTarget) {
                                setIsModalOpen(false);
                            }
                        }}
                    >
                        {modalHasBuy && (
                            <div className={activeTab === 'contact' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
                                <ContactForm 
                                    domain={selectedDomain} 
                                    onClose={() => setIsModalOpen(false)} 
                                    isPurchase={true}
                                    onSwitchToRent={modalHasRent ? () => setActiveTab('rent') : undefined}
                                    onSwitchToForward={modalHasForward ? () => setActiveTab('forward') : undefined}
                                />
                            </div>
                        )}
                        
                        {modalHasRent && (
                            <div className={activeTab === 'rent' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
                                <RentForm 
                                    domain={selectedDomain} 
                                    onClose={() => setIsModalOpen(false)} 
                                    onSwitchToBuy={modalHasBuy ? () => setActiveTab('contact') : undefined}
                                    onSwitchToForward={modalHasForward ? () => setActiveTab('forward') : undefined}
                                />
                            </div>
                        )}

                        {modalHasForward && (
                            <div className={activeTab === 'forward' ? "w-full max-w-lg md:max-w-xl flex justify-center" : "hidden"}>
                                <ForwardForm 
                                    domain={selectedDomain} 
                                    onClose={() => setIsModalOpen(false)} 
                                    onSwitchToRent={modalHasRent ? () => setActiveTab('rent') : undefined}
                                    onSwitchToBuy={modalHasBuy ? () => setActiveTab('contact') : undefined}
                                />
                            </div>
                        )}
                    </div>
                );
            })()}
        </section>
    );
}

const SearchAndFilters = ({ 
    inputRef,
    searchTerm, setSearchTerm, 
    sortBy, setSortBy,
    viewMode, setViewMode,
    isMobile,
    resultsCount,
    availableTags,
    onTagClick,
    onTriggerWhois,
    whoisConfig,
    isWhoisEnabled,
    canSearchRegistry,
    normalizedTargetDomain,
    whoisLoading,
    isWhoisMode,
    onReset
}) => {
    const textMeasureRef = useRef(null);
    const [textWidth, setTextWidth] = useState(0);

    useLayoutEffect(() => {
        if (textMeasureRef.current) {
            setTextWidth(textMeasureRef.current.offsetWidth);
        }
    }, [searchTerm]);

    return (
        <div className="space-y-5 max-w-4xl mx-auto">
            {/* Centered Large Floating Search Bar */}
            <div className="max-w-3xl mx-auto w-full px-2">
                <div className="group relative">
                    {/* Subtle ambient soft backdrop glow (monochrome, expands smoothly on hover/focus) */}
                    <div 
                        className="safari-glow-fix absolute -inset-1 sm:-inset-1.5 rounded-full blur-2xl opacity-10 group-hover:opacity-20 group-focus-within:opacity-40 transition-all duration-500 pointer-events-none"
                        style={{
                            background: 'radial-gradient(ellipse at center, rgba(255, 255, 255, 0.15) 0%, transparent 70%)'
                        }}
                    />

                    {/* Floating Container: matches card tone, elevates on focus with deep drop shadow like lifting off */}
                    <div 
                        onClick={(e) => {
                            if (e.target.tagName !== 'BUTTON' && !e.target.closest('button')) {
                                inputRef?.current?.focus();
                            }
                        }}
                        style={{
                            background: 'linear-gradient(135deg, rgba(var(--card-mid-rgb, 20, 21, 27), 0.95) 0%, rgba(var(--card-mid-rgb, 14, 15, 19), 0.92) 100%)'
                        }}
                        className="relative flex items-center border border-white/[0.12] hover:border-white/[0.22] focus-within:border-white/[0.35] rounded-full shadow-[0_8px_25px_rgba(0,0,0,0.45),inset_0_1px_1px_rgba(255,255,255,0.14)] hover:shadow-[0_12px_32px_rgba(0,0,0,0.55),inset_0_1px_1px_rgba(255,255,255,0.18)] focus-within:shadow-[0_22px_55px_rgba(0,0,0,0.8),0_10px_22px_rgba(0,0,0,0.6),inset_0_1px_2px_rgba(255,255,255,0.25)] backdrop-blur-2xl px-4 sm:px-5 py-2.5 sm:py-3 transform-gpu transition-all duration-300 ease-out hover:-translate-y-0.5 focus-within:-translate-y-2 cursor-text"
                    >
                        {/* Search Icon: dark graphite at rest, cleanly brightening to subtle silver */}
                        <div className="text-neutral-400 group-hover:text-neutral-300 group-focus-within:text-white flex items-center pr-3 pointer-events-none transition-all duration-300">
                            <SearchIcon className="w-5 h-5 sm:w-6 sm:h-6 transition-all duration-300 group-focus-within:scale-105 opacity-80 group-hover:opacity-95 group-focus-within:opacity-100" />
                        </div>

                        {/* Input with text-following clear cross */}
                        <div className="relative flex-1 flex items-center min-w-0">
                            {/* Hidden mirror span to accurately measure the typed word width */}
                            <span 
                                ref={textMeasureRef} 
                                aria-hidden="true" 
                                className="invisible absolute left-0 whitespace-pre pointer-events-none select-none text-sm sm:text-base md:text-lg font-medium tracking-wide"
                            >
                                {searchTerm}
                            </span>

                            <input 
                                ref={inputRef}
                                type="text" 
                                maxLength={30}
                                className="w-full bg-transparent border-0 outline-none text-white placeholder-neutral-500 text-sm sm:text-base md:text-lg font-medium tracking-wide pr-8" 
                                placeholder="Search premium domains..." 
                                value={searchTerm}
                                onChange={(e) => {
                                    let val = e.target.value.slice(0, 30);
                                    val = val.replace(/\s{2,}/g, ' ');
                                    setSearchTerm(val);
                                }}
                                onKeyDown={(e) => {
                                    if (e.key === 'Escape' && (searchTerm || sortBy)) {
                                        e.preventDefault();
                                        if (onReset) {
                                            onReset();
                                        } else {
                                            setSearchTerm('');
                                            setSortBy('');
                                            inputRef?.current?.focus();
                                        }
                                    } else if (e.key === 'Enter' && isWhoisEnabled && canSearchRegistry) {
                                        e.preventDefault();
                                        onTriggerWhois(normalizedTargetDomain?.punycode || normalizedTargetDomain);
                                    }
                                }}
                            />

                            {/* Clear cross: placed almost immediately after the typed word with an offset */}
                            {searchTerm && (
                                <button
                                    type="button"
                                    onClick={() => {
                                        setSearchTerm('');
                                        inputRef?.current?.focus();
                                    }}
                                    style={{
                                        left: `min(calc(100% - 24px), ${textWidth + 10}px)`
                                    }}
                                    className="absolute top-1/2 -translate-y-1/2 w-5 h-5 rounded-full bg-white/10 hover:bg-white/25 text-neutral-400 hover:text-white flex items-center justify-center transition-all cursor-pointer z-10"
                                    title="Clear search"
                                >
                                    <CrossIcon className="w-2.5 h-2.5" />
                                </button>
                            )}
                        </div>

                        {/* Right controls: round search button (visible ONLY when valid domain and WHOIS enabled) */}
                        <div className="flex items-center gap-1.5 sm:gap-2 shrink-0 ml-2">
                            {isWhoisEnabled && canSearchRegistry && (
                                <button
                                    type="button"
                                    onClick={() => onTriggerWhois(normalizedTargetDomain?.punycode || normalizedTargetDomain)}
                                    disabled={whoisLoading}
                                    className="w-9 h-9 sm:w-11 sm:h-11 rounded-full bg-gradient-to-r from-primary-500 to-indigo-600 hover:from-primary-600 hover:to-indigo-500 active:scale-90 text-white flex items-center justify-center shadow-lg shadow-primary-500/30 hover:shadow-primary-500/50 hover:scale-105 transition-all duration-200 cursor-pointer group/btn"
                                    title={`Check ${normalizedTargetDomain?.unicode || normalizedTargetDomain} availability`}
                                >
                                    {whoisLoading ? (
                                        <RefreshIcon className="w-4 h-4 sm:w-5 sm:h-5 animate-spin" />
                                    ) : (
                                        <SearchIcon className="w-4 h-4 sm:w-5 sm:h-5 transition-transform group-hover/btn:scale-110" />
                                    )}
                                </button>
                            )}
                        </div>
                    </div>

                    {/* Live IDN Punycode Preview (shown only when non-Latin / IDN characters are typed) */}
                    {normalizedTargetDomain?.isIdn && !whoisLoading && (
                        <div className="text-right mt-1.5 px-3 text-xs text-neutral-400 font-mono flex items-center justify-end gap-1.5">
                            <span className="text-neutral-500">IDN Punycode:</span>
                            <span className="text-sky-400 font-semibold">{normalizedTargetDomain.unicode}</span>
                            <span className="text-neutral-600">→</span>
                            <span className="text-neutral-300 font-normal">({normalizedTargetDomain.punycode})</span>
                        </div>
                    )}
                </div>
            </div>

            {/* Dynamic Category / Hashtag Chips Bar (hidden during WHOIS mode) */}
            {!isWhoisMode && availableTags.length > 0 && (
                <div className="flex items-center gap-1.5 overflow-x-auto pb-1 pt-0.5 no-scrollbar text-xs justify-center flex-wrap sm:flex-nowrap">
                    <span className="text-neutral-500 font-semibold shrink-0 text-[11px] uppercase tracking-wider flex items-center gap-1 mr-1">
                        <TagIcon className="w-3 h-3 text-primary-400" />
                        <span>Tags:</span>
                    </span>

                    <button
                        onClick={() => {
                            onTagClick('');
                            inputRef?.current?.focus();
                        }}
                        className={`px-2.5 py-1 rounded-lg border font-medium transition-all shrink-0 cursor-pointer ${
                            !searchTerm 
                                ? 'bg-primary-500 text-white border-primary-500 shadow-sm' 
                                : 'bg-dark-200/60 text-neutral-400 hover:text-white border-white/5 hover:border-white/10'
                        }`}
                    >
                        All
                    </button>

                    {availableTags.slice(0, 12).map((tag) => {
                        const isSelected = searchTerm.toLowerCase() === `#${tag.toLowerCase()}` || searchTerm.toLowerCase() === tag.toLowerCase();
                        return (
                            <button
                                key={tag}
                                onClick={() => {
                                    onTagClick(tag);
                                    inputRef?.current?.focus();
                                }}
                                className={`px-2.5 py-1 rounded-lg border font-medium transition-all shrink-0 cursor-pointer ${
                                    isSelected
                                        ? 'bg-primary-500 text-white border-primary-500 shadow-sm'
                                        : 'bg-dark-200/60 text-neutral-300 hover:text-primary-300 border-white/5 hover:border-primary-500/30'
                                }`}
                            >
                                #{tag}
                            </button>
                        );
                    })}
                </div>
            )}

            {/* Results Count, Reset Filters, Compact Sort By & View Toggle Row */}
            {/* When 0 domains are found OR in WHOIS mode: completely hide this entire strip! */}
            {!isWhoisMode && resultsCount > 0 && (
                <div className="flex flex-wrap items-center justify-between gap-3 pt-2 px-2">
                    <div className="flex items-center gap-2">
                        {(searchTerm || sortBy) ? (
                            <>
                                <p className="text-sm text-neutral-300">
                                    Found <span className="text-white font-semibold">{resultsCount}</span> {resultsCount === 1 ? 'premium domain' : 'premium domains'}
                                </p>
                                <button
                                    type="button"
                                    onClick={() => {
                                        if (onReset) {
                                            onReset();
                                        } else {
                                            setSearchTerm('');
                                            setSortBy('');
                                            inputRef?.current?.focus();
                                        }
                                    }}
                                    className="w-5 h-5 rounded-full bg-white/5 hover:bg-white/15 text-neutral-400 hover:text-white flex items-center justify-center transition-all cursor-pointer ml-1"
                                    title="Reset filters"
                                >
                                    <CrossIcon className="w-2.5 h-2.5" />
                                </button>
                            </>
                        ) : (
                            <p className="text-xs text-neutral-400">
                                Total <span className="text-white font-semibold">{resultsCount}</span> premium domains
                            </p>
                        )}
                    </div>
                    
                    {/* Right side: Compact Sort by + View Toggle pushed to top-right */}
                    <div className="flex items-center gap-2 ml-auto">
                        {/* Compact Sort By */}
                        <div className="flex items-center gap-1.5 bg-dark-200/70 border border-white/10 rounded-xl px-2.5 py-1 text-xs text-neutral-400 hover:border-white/20 transition-colors shadow-sm">
                            <select 
                                value={sortBy}
                                onChange={(e) => setSortBy(e.target.value)}
                                className="bg-transparent text-white text-xs font-medium outline-none cursor-pointer pr-1 appearance-none"
                            >
                                <option value="" className="bg-dark-100 text-white">Sort: Default</option>
                                <option value="price-low" className="bg-dark-100 text-white">Price: Low to High</option>
                                <option value="price-high" className="bg-dark-100 text-white">Price: High to Low</option>
                                <option value="name-az" className="bg-dark-100 text-white">Name: A to Z</option>
                                <option value="name-za" className="bg-dark-100 text-white">Name: Z to A</option>
                            </select>
                            <ChevronDownIcon className="w-3 h-3 text-neutral-500 pointer-events-none" />
                        </div>

                        {/* View Toggle: ONLY icons, subtle non-aggressive highlight */}
                        <div className="hidden md:flex rounded-xl overflow-hidden bg-dark-200/70 p-0.5 shadow-sm">
                            <button
                                type="button"
                                onClick={() => setViewMode('grid')}
                                className={`p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center ${
                                    viewMode === 'grid' 
                                        ? 'bg-white/15 text-white shadow-sm border border-white/10' 
                                        : 'text-neutral-400 hover:text-white'
                                }`}
                                title="Grid View"
                                aria-label="Grid View"
                            >
                                <div className="w-3.5 h-3.5 grid grid-cols-2 gap-0.5">
                                    <div className="bg-current rounded-sm"></div>
                                    <div className="bg-current rounded-sm"></div>
                                    <div className="bg-current rounded-sm"></div>
                                    <div className="bg-current rounded-sm"></div>
                                </div>
                            </button>
                            <button
                                type="button"
                                onClick={() => setViewMode('table')}
                                className={`p-1.5 rounded-lg transition-all cursor-pointer flex items-center justify-center ${
                                    viewMode === 'table' 
                                        ? 'bg-white/15 text-white shadow-sm border border-white/10' 
                                        : 'text-neutral-400 hover:text-white'
                                }`}
                                title="Table View"
                                aria-label="Table View"
                            >
                                <div className="w-3.5 h-3.5 flex flex-col gap-0.5 justify-center">
                                    <div className="h-0.5 bg-current w-full rounded-sm"></div>
                                    <div className="h-0.5 bg-current w-full rounded-sm"></div>
                                    <div className="h-0.5 bg-current w-full rounded-sm"></div>
                                </div>
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

const DomainSkeleton = ({ viewMode = 'grid' }) => {
    return (
        <div className="w-full space-y-6 animate-fade-simple">
            {/* Shimmer cards grid or table */}
            {viewMode === 'table' ? (
                <div className="rounded-3xl border-0 bg-white/[0.02] overflow-hidden p-3 sm:p-4 space-y-3">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                        <div key={i} className="flex items-center justify-between py-3 px-4 border-b border-white/[0.04] last:border-none">
                            <div className="space-y-2">
                                <div className="h-5 w-44 rounded-lg bg-white/[0.06] skeleton-shimmer" />
                                <div className="h-3.5 w-64 rounded bg-white/[0.03] skeleton-shimmer" />
                            </div>
                        </div>
                    ))}
                </div>
            ) : (
                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 w-full">
                    {[1, 2, 3, 4, 5, 6, 7, 8, 9].map((i) => (
                        <div
                            key={i}
                            className="rounded-3xl border-0 bg-neutral-900/40 p-6 space-y-4 shadow-xl overflow-hidden relative"
                        >
                            <div className="flex items-start justify-between relative z-10">
                                <div className="space-y-2">
                                    <div className="h-6 w-36 rounded-lg bg-white/[0.06] skeleton-shimmer" />
                                    <div className="h-1 w-12 rounded-full bg-white/[0.04]" />
                                </div>
                                <div className="h-5 w-14 rounded-full bg-white/[0.04] skeleton-shimmer" />
                            </div>
                            <div className="space-y-2 pt-1 relative z-10">
                                <div className="h-3.5 w-full rounded bg-white/[0.03] skeleton-shimmer" />
                                <div className="h-3.5 w-2/3 rounded bg-white/[0.03] skeleton-shimmer" />
                            </div>
                            <div className="flex gap-1.5 pt-1 relative z-10">
                                <div className="h-5 w-14 rounded-md bg-white/[0.03] skeleton-shimmer" />
                                <div className="h-5 w-16 rounded-md bg-white/[0.03] skeleton-shimmer" />
                            </div>
                        </div>
                    ))}
                </div>
            )}
        </div>
    );
};

export const WhoisSkeletonCard = ({ queueElapsed, targetDomain }) => (
    <div className="relative rounded-3xl bg-dark-100/90 border-0 shadow-2xl p-6 sm:p-8 backdrop-blur-xl max-w-3xl mx-auto mb-12 overflow-hidden animate-fade-simple">
        {/* Top Shimmer Header Bar */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6 pb-6 border-b border-white/5">
            <div className="space-y-3">
                <div className="flex items-center gap-3">
                    {targetDomain ? (
                        <h2 className="text-2xl sm:text-3xl font-extrabold text-white font-display">
                            {targetDomain}
                        </h2>
                    ) : (
                        <div className="h-8 w-56 sm:w-72 skeleton-shimmer rounded-xl" />
                    )}
                </div>
            </div>

            <div className="flex items-center sm:justify-end">
                <div className="h-6 w-28 rounded-full bg-white/[0.04] skeleton-shimmer" />
            </div>
        </div>

        {/* 3 Structured Metadata Boxes with Pure Skeleton Shimmer (no static text labels) */}
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4 mb-6">
            <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5 space-y-2.5">
                <div className="h-3 w-16 skeleton-shimmer rounded" />
                <div className="h-5 w-28 skeleton-shimmer rounded-md" />
            </div>
            <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5 space-y-2.5">
                <div className="h-3 w-24 skeleton-shimmer rounded" />
                <div className="h-5 w-24 skeleton-shimmer rounded-md" />
            </div>
            <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5 space-y-2.5">
                <div className="h-3 w-24 skeleton-shimmer rounded" />
                <div className="h-5 w-24 skeleton-shimmer rounded-md" />
            </div>
        </div>

        {/* Name Servers Skeleton (no static text labels) */}
        <div className="p-3.5 rounded-xl bg-dark-200/50 border border-white/5 space-y-2.5">
            <div className="flex items-center gap-2">
                <div className="w-3.5 h-3.5 rounded-full skeleton-shimmer shrink-0" />
                <div className="h-3 w-24 skeleton-shimmer rounded" />
            </div>
            <div className="flex flex-wrap gap-2 pt-1">
                <div className="h-6 w-36 skeleton-shimmer rounded-md" />
                <div className="h-6 w-40 skeleton-shimmer rounded-md" />
            </div>
        </div>
    </div>
);

function DomainList({ domains, viewMode, isMobile, setShowContactForm, setShowRentForm, setShowForwardForm, setSelectedDomain, onSelectTag }) {
    const effectiveView = isMobile ? 'grid' : viewMode;

    if (effectiveView === 'table') {
        return (
            <DomainTable 
                domains={domains} 
                setShowContactForm={setShowContactForm} 
                setShowRentForm={setShowRentForm} 
                setShowForwardForm={setShowForwardForm} 
                setSelectedDomain={setSelectedDomain} 
                onSelectTag={onSelectTag}
            />
        );
    }
    return (
        <DomainCard 
            domains={domains} 
            setShowContactForm={setShowContactForm} 
            setShowRentForm={setShowRentForm} 
            setShowForwardForm={setShowForwardForm} 
            setSelectedDomain={setSelectedDomain} 
            onSelectTag={onSelectTag}
        />
    );
}

const DomainTable = ({ domains, setShowContactForm, setShowRentForm, setShowForwardForm, setSelectedDomain, onSelectTag }) => (
    <div className="relative rounded-3xl border border-white/[0.12] bg-gradient-to-b from-white/[0.07] via-white/[0.015] to-white/[0.035] backdrop-blur-2xl shadow-[inset_0_1px_1px_rgba(255,255,255,0.2),0_12px_36px_rgba(0,0,0,0.3)] overflow-hidden">
        <div className="overflow-x-auto">
            <table className="w-full">
                <thead>
                    <tr className="border-b border-white/10 bg-white/[0.02]">
                        <th className="text-left py-4 px-6 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                            Domain
                        </th>
                        <th className="text-right py-4 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                            <span className="inline-flex items-center gap-1.5">
                                <BuyIcon className="w-3 h-3 text-neutral-400" />
                                <span>Buy Price</span>
                            </span>
                        </th>
                        <th className="text-right py-4 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                            <span className="inline-flex items-center gap-1.5">
                                <RentIcon className="w-3 h-3 text-neutral-400" />
                                <span>Rent /mo</span>
                            </span>
                        </th>
                        <th className="text-right py-4 px-4 text-xs font-semibold text-neutral-400 uppercase tracking-wider">
                            <span className="inline-flex items-center gap-1.5">
                                <ForwardIcon className="w-3 h-3 text-neutral-400" />
                                <span>Fw /day</span>
                            </span>
                        </th>
                    </tr>
                </thead>
                <tbody className="divide-y divide-white/5">
                    {domains.map((item) => (
                        <DomainRow 
                            key={item.id} 
                            item={item} 
                            setShowContactForm={setShowContactForm} 
                            setShowRentForm={setShowRentForm} 
                            setShowForwardForm={setShowForwardForm} 
                            setSelectedDomain={setSelectedDomain} 
                            onSelectTag={onSelectTag}
                        />
                    ))}
                </tbody>
            </table>
        </div>
    </div>
);

const DomainRow = ({ item, setShowContactForm, setShowRentForm, setShowForwardForm, setSelectedDomain, onSelectTag }) => {
    const navigate = useNavigate();
    const now = new Date();
    const rentUntil = item.rent_until ? new Date(item.rent_until) : null;
    const forwardUntil = item.forward_until ? new Date(item.forward_until) : null;
    
    let busyStatus = null;
    if (item.is_sold > 0) busyStatus = { type: 'sold', label: 'Sold', color: 'bg-red-500/20 text-red-400 border-red-500/30' };
    else if (rentUntil && rentUntil > now) busyStatus = { type: 'rented', label: `Busy until ${rentUntil.toLocaleDateString()}`, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
    else if (forwardUntil && forwardUntil > now) busyStatus = { type: 'forwarded', label: `Busy until ${forwardUntil.toLocaleDateString()}`, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };

    const hasBuy = Boolean(item.pricebuy && Number(item.pricebuy) > 0);
    const hasRent = Boolean(item.pricerent && Number(item.pricerent) > 0);
    const hasForward = Boolean(item.pricefw && Number(item.pricefw) > 0);

    const hashtags = extractHashtags(item.description);
    const cleanDesc = stripHashtags(item.description) || item.description;

    return (
        <tr 
            className={`border-b border-dark-300/50 transition-colors group cursor-pointer relative animate-card-graceful ${busyStatus ? 'opacity-60 grayscale-[30%] hover:opacity-80' : 'hover:bg-dark-200/30'}`}
            onClick={() => navigate(`/${item.name}.html`)}
        >
            <td className="py-5 px-6">
                <Link 
                    to={`/${item.name}.html`} 
                    className="block"
                    onClick={(e) => e.stopPropagation()}
                >
                    <div>
                        <div className="flex items-center gap-3 mb-1">
                            <div className="inline-flex flex-col items-start max-w-full">
                                <h3 className="text-xl font-display font-bold text-white break-words tracking-tight domain-spotlight-text">
                                    {item.name}
                                </h3>
                                <div 
                                    className="h-[2.5px] w-8 group-hover:w-[92%] mt-1.5 mb-2 rounded-full opacity-70 group-hover:opacity-100 transition-all duration-500 ease-out shadow-[0_0_14px_rgba(255,255,255,0.4)]"
                                    style={{
                                        background: 'linear-gradient(90deg, rgba(255,255,255,0.8), rgba(255,255,255,0.3))'
                                    }}
                                />
                            </div>
                            {busyStatus && (
                                <span className={`px-2 py-0.5 rounded-full text-[10px] font-bold border uppercase tracking-wider ${busyStatus.color}`}>
                                    {busyStatus.label}
                                </span>
                            )}
                        </div>
                        <p className="text-sm text-neutral-400 break-words leading-relaxed">{cleanDesc}</p>
                        
                        {/* Hashtag Badges */}
                        {hashtags.length > 0 && (
                            <div className="flex flex-wrap gap-1 mt-2">
                                {hashtags.map((tag, tIdx) => (
                                    <button
                                        key={tIdx}
                                        type="button"
                                        onClick={(e) => {
                                            e.preventDefault();
                                            e.stopPropagation();
                                            if (onSelectTag) onSelectTag(tag);
                                        }}
                                        className="px-2 py-0.5 rounded-md text-[10px] font-medium bg-white/[0.05] text-neutral-300 border border-white/10 hover:border-white/25 hover:text-white transition-all hover:scale-105"
                                    >
                                        #{tag}
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </Link>
            </td>
            <td className="py-3 px-4 text-right align-middle">
                {hasBuy ? (
                    <button
                        type="button"
                        onClick={!(item.is_sold > 0) ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedDomain(item);
                            setShowContactForm(true);
                        } : undefined}
                        disabled={item.is_sold > 0}
                        className={`inline-flex flex-col items-center justify-center min-w-[92px] py-1.5 px-3 rounded-xl border transition-all duration-200 group/buy ${
                            !(item.is_sold > 0)
                                ? 'bg-neutral-950/60 hover:bg-neutral-900/90 border-white/[0.08] hover:border-white/[0.22] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_14px_rgba(255,255,255,0.12)] backdrop-blur-md cursor-pointer active:scale-95'
                                : 'bg-neutral-950/40 border-white/[0.04] opacity-50 cursor-not-allowed'
                        }`}
                        title={!(item.is_sold > 0) ? `Buy ${item.name} for $${Number(item.pricebuy).toLocaleString()}` : undefined}
                    >
                        <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 group-hover/buy:text-white transition-colors">
                            <BuyIcon className="w-3 h-3 text-neutral-400 group-hover/buy:text-white shrink-0 group-hover/buy:animate-cart-fill transition-colors" />
                            <span>Buy</span>
                        </div>
                        <div className="flex items-baseline justify-center gap-1 mt-0.5">
                            <span className="text-sm sm:text-base font-bold font-mono text-white transition-colors" title={`$${Number(item.discount_buy > 0 ? (item.pricebuy * (1 - item.discount_buy / 100)) : item.pricebuy).toLocaleString()}`}>
                                ${formatCompactPrice(item.discount_buy > 0 ? (item.pricebuy * (1 - item.discount_buy / 100)) : item.pricebuy)}
                            </span>
                            {item.discount_buy > 0 && (
                                <span className="text-[10px] font-mono line-through text-neutral-500" title={`$${Number(item.pricebuy).toLocaleString()}`}>
                                    ${formatCompactPrice(item.pricebuy)}
                                </span>
                            )}
                        </div>
                    </button>
                ) : (
                    <span className="text-neutral-600 text-sm font-mono pr-4">—</span>
                )}
            </td>
            <td className="py-3 px-4 text-right align-middle">
                {hasRent ? (
                    <button
                        type="button"
                        onClick={!(item.is_sold > 0) ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedDomain(item);
                            setShowRentForm(true);
                        } : undefined}
                        disabled={item.is_sold > 0}
                        className={`inline-flex flex-col items-center justify-center min-w-[92px] py-1.5 px-3 rounded-xl border transition-all duration-200 group/rent ${
                            !(item.is_sold > 0)
                                ? 'bg-neutral-950/60 hover:bg-neutral-900/90 border-white/[0.08] hover:border-white/[0.22] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_14px_rgba(255,255,255,0.12)] backdrop-blur-md cursor-pointer active:scale-95'
                                : 'bg-neutral-950/40 border-white/[0.04] opacity-50 cursor-not-allowed'
                        }`}
                        title={!(item.is_sold > 0) ? `Rent ${item.name} for $${Number(item.pricerent).toLocaleString()}/mo` : undefined}
                    >
                        <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 group-hover/rent:text-white transition-colors">
                            <RentIcon className="w-3 h-3 text-neutral-400 group-hover/rent:text-white shrink-0 group-hover/rent:animate-key-turn transition-colors" />
                            <span>Rent</span>
                        </div>
                        <div className="flex items-baseline justify-center gap-0.5 mt-0.5">
                            <span className="text-sm sm:text-base font-bold font-mono text-white transition-colors" title={`$${Number(item.discount_rent > 0 ? (item.pricerent * (1 - item.discount_rent / 100)) : item.pricerent).toLocaleString()}/mo`}>
                                ${formatCompactPrice(item.discount_rent > 0 ? (item.pricerent * (1 - item.discount_rent / 100)) : item.pricerent)}
                            </span>
                            <span className="text-[10px] text-neutral-500 font-sans">/mo</span>
                            {item.discount_rent > 0 && (
                                <span className="text-[10px] font-mono line-through text-neutral-500 ml-0.5" title={`$${Number(item.pricerent).toLocaleString()}/mo`}>
                                    ${formatCompactPrice(item.pricerent)}
                                </span>
                            )}
                        </div>
                    </button>
                ) : (
                    <span className="text-neutral-600 text-sm font-mono pr-4">—</span>
                )}
            </td>
            <td className="py-3 px-4 text-right align-middle">
                {hasForward ? (
                    <button
                        type="button"
                        onClick={!(item.is_sold > 0) ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setSelectedDomain(item);
                            setShowForwardForm(true);
                        } : undefined}
                        disabled={item.is_sold > 0}
                        className={`inline-flex flex-col items-center justify-center min-w-[92px] py-1.5 px-3 rounded-xl border transition-all duration-200 group/fw ${
                            !(item.is_sold > 0)
                                ? 'bg-neutral-950/60 hover:bg-neutral-900/90 border-white/[0.08] hover:border-white/[0.22] hover:shadow-[inset_0_1px_1px_rgba(255,255,255,0.1),0_0_14px_rgba(255,255,255,0.12)] backdrop-blur-md cursor-pointer active:scale-95'
                                : 'bg-neutral-950/40 border-white/[0.04] opacity-50 cursor-not-allowed'
                        }`}
                        title={!(item.is_sold > 0) ? `Forward ${item.name} for $${Number(item.pricefw).toLocaleString()}/day` : undefined}
                    >
                        <div className="flex items-center justify-center gap-1 text-[10px] font-semibold uppercase tracking-wider text-neutral-400 group-hover/fw:text-white transition-colors">
                            <ForwardIcon className="w-3 h-3 text-neutral-400 group-hover/fw:text-white shrink-0 group-hover/fw:animate-pulse transition-colors" />
                            <span>Forward</span>
                        </div>
                        <div className="flex items-baseline justify-center gap-0.5 mt-0.5">
                            <span className="text-sm sm:text-base font-bold font-mono text-white transition-colors" title={`$${Number(item.discount_fw > 0 ? (item.pricefw * (1 - item.discount_fw / 100)) : item.pricefw).toLocaleString()}/day`}>
                                ${formatCompactPrice(item.discount_fw > 0 ? (item.pricefw * (1 - item.discount_fw / 100)) : item.pricefw)}
                            </span>
                            <span className="text-[10px] text-neutral-500 font-sans">/day</span>
                            {item.discount_fw > 0 && (
                                <span className="text-[10px] font-mono line-through text-neutral-500 ml-0.5" title={`$${Number(item.pricefw).toLocaleString()}/day`}>
                                    ${formatCompactPrice(item.pricefw)}
                                </span>
                            )}
                        </div>
                    </button>
                ) : (
                    <span className="text-neutral-600 text-sm font-mono pr-4">—</span>
                )}
            </td>
        </tr>
    );
};

const DomainCard = ({ domains, setShowContactForm, setShowRentForm, setShowForwardForm, setSelectedDomain, onSelectTag }) => {
    const gridLayout = domains.length === 1
        ? 'grid grid-cols-1 max-w-md mx-auto gap-6 w-full'
        : domains.length === 2
            ? 'grid grid-cols-1 md:grid-cols-2 max-w-3xl mx-auto gap-6 w-full'
            : 'grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6 w-full';

    return (
        <div className={gridLayout}>
            {domains.map((item) => (
                <div key={item.id} className="w-full flex animate-card-graceful">
                    <DomainCardItem 
                        item={item} 
                        setShowContactForm={setShowContactForm} 
                        setShowRentForm={setShowRentForm} 
                        setShowForwardForm={setShowForwardForm} 
                        setSelectedDomain={setSelectedDomain} 
                        onSelectTag={onSelectTag}
                    />
                </div>
            ))}
        </div>
    );
};

export const DomainCardItem = ({ item, setShowContactForm, setShowRentForm, setShowForwardForm, setSelectedDomain, onSelectTag, compact = false }) => {
    const now = new Date();
    const rentUntil = item.rent_until ? new Date(item.rent_until) : null;
    const forwardUntil = item.forward_until ? new Date(item.forward_until) : null;
    
    // Interactive 3D Cursor & Touch Tilt with Specular Reflection
    const [tilt, setTilt] = useState({ rotateX: 0, rotateY: 0, shineX: 50, shineY: 50, isHovered: false });
    const cardRef = useRef(null);

    const handlePointerMove = useCallback((clientX, clientY) => {
        if (!cardRef.current) return;
        const rect = cardRef.current.getBoundingClientRect();
        const x = clientX - rect.left;
        const y = clientY - rect.top;

        if (x < 0 || y < 0 || x > rect.width || y > rect.height) return;

        const xPercent = (x / rect.width) * 2 - 1;
        const yPercent = (y / rect.height) * 2 - 1;

        // Elegant subtle tilt: max 6 degrees for standard cards
        const maxTilt = 6;
        const rotateX = -yPercent * maxTilt;
        const rotateY = xPercent * maxTilt;

        setTilt({
            rotateX,
            rotateY,
            shineX: (x / rect.width) * 100,
            shineY: (y / rect.height) * 100,
            isHovered: true
        });
    }, []);

    const handleMouseMove = useCallback((e) => {
        handlePointerMove(e.clientX, e.clientY);
    }, [handlePointerMove]);

    const handleTouchMove = useCallback((e) => {
        if (e.touches && e.touches[0]) {
            handlePointerMove(e.touches[0].clientX, e.touches[0].clientY);
        }
    }, [handlePointerMove]);

    const handlePointerLeave = useCallback(() => {
        setTilt({ rotateX: 0, rotateY: 0, shineX: 50, shineY: 50, isHovered: false });
    }, []);

    let busyStatus = null;
    if (item.is_sold > 0) busyStatus = { type: 'sold', label: 'Sold', color: 'bg-red-500/20 text-red-400 border-red-500/30' };
    else if (rentUntil && rentUntil > now) busyStatus = { type: 'rented', label: `Busy until ${rentUntil.toLocaleDateString()}`, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };
    else if (forwardUntil && forwardUntil > now) busyStatus = { type: 'forwarded', label: `Busy until ${forwardUntil.toLocaleDateString()}`, color: 'bg-orange-500/20 text-orange-400 border-orange-500/30' };

    const hasBuy = Boolean(item.pricebuy && Number(item.pricebuy) > 0);
    const hasRent = Boolean(item.pricerent && Number(item.pricerent) > 0);
    const hasForward = Boolean(item.pricefw && Number(item.pricefw) > 0);
    const enabledCount = [hasBuy, hasRent, hasForward].filter(Boolean).length;

    const hashtags = extractHashtags(item.description);
    const cleanDesc = stripHashtags(item.description) || item.description;

    return (
        <div className="w-full h-full" style={{ perspective: '900px' }}>
            <Link 
                to={`/${item.name}.html`} 
                ref={cardRef}
                onMouseMove={handleMouseMove}
                onMouseLeave={handlePointerLeave}
                onTouchMove={handleTouchMove}
                onTouchEnd={handlePointerLeave}
                onTouchCancel={handlePointerLeave}
                style={{
                    '--light-x': `${tilt.shineX}%`,
                    '--light-y': `${tilt.shineY}%`,
                    '--light-opacity': tilt.isHovered ? '1' : '0',
                    transform: `rotateX(${tilt.rotateX}deg) rotateY(${tilt.rotateY}deg) ${tilt.isHovered ? 'translateY(-7px) scale(1.025)' : 'translateY(0px) scale(1)'}`,
                    transformStyle: 'preserve-3d',
                    boxShadow: tilt.isHovered
                        ? 'inset 0 -1px 2px 0 rgba(0, 0, 0, 0.8), 0 24px 44px -10px rgba(0, 0, 0, 0.85), 0 0 24px -4px rgba(255, 255, 255, 0.05)'
                        : 'inset 0 -1px 1px 0 rgba(0, 0, 0, 0.7), 0 14px 36px -10px rgba(0, 0, 0, 0.7)',
                    transition: tilt.isHovered ? 'transform 0.12s cubic-bezier(0.2, 0, 0, 1), box-shadow 0.25s ease' : 'transform 0.55s cubic-bezier(0.16, 1, 0.3, 1), box-shadow 0.4s ease'
                }}
                className={`group cursor-pointer ${compact ? 'p-4 sm:p-5 rounded-2xl' : 'p-6 sm:p-7 rounded-3xl'} w-full h-full flex flex-col justify-between relative overflow-hidden ${
                    busyStatus ? 'opacity-60 grayscale-[30%] hover:opacity-80' : ''
                } metallic-card no-hover will-change-transform [-webkit-mask-image:-webkit-radial-gradient(white,black)]`}
            >
                {/* Flashlight Border Sheen: illuminates outer card border directly beneath the light cursor */}
                <div 
                    className={`absolute inset-0 pointer-events-none ${compact ? 'rounded-2xl' : 'rounded-3xl'} z-20 transition-opacity duration-300`}
                    style={{
                        opacity: tilt.isHovered ? 1 : 0,
                        padding: '1px',
                        background: `radial-gradient(circle 280px at ${tilt.shineX}% ${tilt.shineY}%, rgba(255, 255, 255, 0.5) 0%, rgba(255, 255, 255, 0.12) 35%, transparent 70%)`,
                        WebkitMask: 'linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0)',
                        WebkitMaskComposite: 'xor',
                        maskComposite: 'exclude'
                    }}
                />

                {/* Dynamic Specular Light Glare following cursor or touch */}
                <div 
                    className="absolute inset-0 pointer-events-none transition-opacity duration-300 z-10"
                    style={{
                        opacity: tilt.isHovered ? 0.3 : 0,
                        background: `radial-gradient(circle 350px at ${tilt.shineX}% ${tilt.shineY}%, rgba(255, 255, 255, 0.28) 0%, rgba(255, 255, 255, 0.06) 45%, transparent 70%)`
                    }}
                />

                {/* Brushed metallic hairlines texture across entire card */}
                <div className="absolute inset-0 metallic-hairlines opacity-70 pointer-events-none" />

            {/* Full-width continuous titanium plate surface gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-white/[0.045] via-transparent to-white/[0.02] pointer-events-none" />

            {/* Subtle monochrome specular sheen in corners (pure titanium neutral) */}
            <div className="absolute top-0 right-0 w-44 h-44 rounded-full blur-2xl pointer-events-none opacity-25 group-hover:opacity-40 transition-opacity duration-500 bg-white/[0.05]" />
            <div className="absolute bottom-0 left-0 w-40 h-40 rounded-full blur-2xl pointer-events-none opacity-15 group-hover:opacity-30 transition-opacity duration-500 bg-white/[0.04]" />

            {/* Beveled Top Specular Edge Sheen */}
            <div className="absolute inset-x-0 top-0 metallic-top-sheen pointer-events-none" />

            {/* Perimeter chamfered light line */}
            <div className="absolute inset-0 rounded-[inherit] border border-white/[0.04] pointer-events-none" />

            {busyStatus && (
                <div className={`absolute top-3.5 right-3.5 px-2.5 py-0.5 rounded-full text-[10px] font-bold border backdrop-blur-md z-10 uppercase tracking-wider ${busyStatus.color}`}>
                    {busyStatus.label}
                </div>
            )}

            {/* Domain Name & Info */}
            <div className={`text-center relative z-10 ${compact ? 'mb-3' : 'mb-5'} flex-1 flex flex-col justify-center items-center`}>
                <div className="inline-flex flex-col items-center max-w-full">
                    <h3 className={`${compact ? 'text-lg sm:text-xl' : 'text-2xl sm:text-[26px]'} font-display font-extrabold break-words tracking-tight mb-1.5 domain-spotlight-text`}>
                        {item.name}
                    </h3>
                    <div 
                        className={`h-[2px] ${compact ? 'w-8 mb-2' : 'w-10 mb-3'} group-hover:w-[92%] rounded-full opacity-60 group-hover:opacity-100 transition-all duration-500 ease-out shadow-[0_0_10px_var(--theme-glow-rgba,rgba(255,255,255,0.2))]`}
                        style={{
                            background: 'linear-gradient(90deg, var(--color-primary-400, #38bdf8), rgba(255,255,255,0.7))'
                        }}
                    />
                </div>
                {cleanDesc && (
                    <p className={`text-neutral-400 group-hover:text-neutral-300 transition-colors ${compact ? 'text-xs line-clamp-1' : 'text-sm leading-relaxed'} break-words font-normal`}>
                        {cleanDesc}
                    </p>
                )}

                {/* Hashtags */}
                {hashtags.length > 0 && !compact && (
                    <div className="flex flex-wrap items-center justify-center gap-1.5 mt-3">
                        {hashtags.map((tag) => (
                            <button
                                key={tag}
                                type="button"
                                onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    if (onSelectTag) onSelectTag(tag);
                                }}
                                className="px-2.5 py-0.5 rounded-full text-[10px] font-medium bg-black/40 hover:bg-neutral-800/90 text-neutral-300 hover:text-white border border-white/[0.08] hover:border-white/[0.22] transition-all hover:scale-105 cursor-pointer backdrop-blur-sm shadow-[inset_0_1px_1px_rgba(255,255,255,0.06)]"
                            >
                                #{tag}
                            </button>
                        ))}
                    </div>
                )}
            </div>

            {/* Pricing Blocks (Precision-milled metallic slots) */}
            <div className={`relative z-10 mt-auto pt-2 grid ${compact ? 'gap-1.5' : 'gap-2 sm:gap-2.5'} ${enabledCount === 1 ? 'grid-cols-1 max-w-xs mx-auto w-full' : enabledCount === 2 ? 'grid-cols-2 w-full' : 'grid-cols-3 w-full'}`}>
                {hasBuy && (
                    <div 
                        onClick={!(item.is_sold > 0) ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (setSelectedDomain) setSelectedDomain(item);
                            if (setShowContactForm) setShowContactForm(true);
                        } : undefined}
                        className={`text-center ${compact ? 'p-2 rounded-xl' : 'p-2.5 sm:p-3 rounded-2xl'} metallic-slot relative ${
                            !(item.is_sold > 0) 
                                ? 'cursor-pointer active:scale-95 group/buy' 
                                : 'opacity-60 cursor-not-allowed'
                        }`}
                        title={!(item.is_sold > 0) ? `Buy ${item.name} for $${Number(item.pricebuy).toLocaleString()}` : undefined}
                    >
                        <div className="flex items-center justify-center gap-1 text-neutral-400 text-[10px] font-semibold uppercase tracking-wider mb-1 group-hover/buy:text-white transition-colors">
                            <BuyIcon className="w-3 h-3 text-neutral-400 group-hover/buy:text-white shrink-0 group-hover/buy:animate-cart-fill transition-colors" />
                            <span>Buy</span>
                        </div>
                        {item.discount_buy > 0 ? (
                            <div className="flex flex-col items-center">
                                <p 
                                    className={`${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} font-bold font-mono text-white transition-colors`}
                                    title={`$${Number(item.pricebuy * (1 - item.discount_buy / 100)).toLocaleString()}`}
                                >
                                    ${formatCompactPrice(item.pricebuy * (1 - item.discount_buy / 100))}
                                </p>
                                <p 
                                    className="text-[10px] font-mono line-through text-neutral-500"
                                    title={`$${Number(item.pricebuy).toLocaleString()}`}
                                >
                                    ${formatCompactPrice(item.pricebuy)}
                                </p>
                            </div>
                        ) : (
                            <p 
                                className={`${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} font-bold font-mono text-white transition-colors`}
                                title={`$${Number(item.pricebuy).toLocaleString()}`}
                            >
                                ${formatCompactPrice(item.pricebuy)}
                            </p>
                        )}
                    </div>
                )}
                {hasRent && (
                    <div 
                        onClick={!(item.is_sold > 0) ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (setSelectedDomain) setSelectedDomain(item);
                            if (setShowRentForm) setShowRentForm(true);
                        } : undefined}
                        className={`text-center ${compact ? 'p-2 rounded-xl' : 'p-2.5 sm:p-3 rounded-2xl'} metallic-slot relative ${
                            !(item.is_sold > 0) 
                                ? 'cursor-pointer active:scale-95 group/rent' 
                                : 'opacity-60 cursor-not-allowed'
                        }`}
                        title={!(item.is_sold > 0) ? `Rent ${item.name} for $${Number(item.pricerent).toLocaleString()}/mo` : undefined}
                    >
                        <div className="flex items-center justify-center gap-1 text-neutral-400 text-[10px] font-semibold uppercase tracking-wider mb-1 group-hover/rent:text-white transition-colors">
                            <RentIcon className="w-3 h-3 text-neutral-400 group-hover/rent:text-white shrink-0 group-hover/rent:animate-clock-fast transition-colors" />
                            <span>Rent</span>
                        </div>
                        {item.discount_rent > 0 ? (
                            <div className="flex flex-col items-center">
                                <p 
                                    className={`${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} font-bold font-mono text-white transition-colors`}
                                    title={`$${Number(item.pricerent * (1 - item.discount_rent / 100)).toLocaleString()}/mo`}
                                >
                                    ${formatCompactPrice(item.pricerent * (1 - item.discount_rent / 100))}<span className="text-[10px] font-normal text-neutral-400">/mo</span>
                                </p>
                                <p 
                                    className="text-[10px] font-mono line-through text-neutral-500"
                                    title={`$${Number(item.pricerent).toLocaleString()}/mo`}
                                >
                                    ${formatCompactPrice(item.pricerent)}
                                </p>
                            </div>
                        ) : (
                            <p 
                                className={`${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} font-bold font-mono text-white transition-colors`}
                                title={`$${Number(item.pricerent).toLocaleString()}/mo`}
                            >
                                ${formatCompactPrice(item.pricerent)}<span className="text-[10px] font-normal text-neutral-400">/mo</span>
                            </p>
                        )}
                    </div>
                )}
                {hasForward && (
                    <div 
                        onClick={!(item.is_sold > 0) ? (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            if (setSelectedDomain) setSelectedDomain(item);
                            if (setShowForwardForm) setShowForwardForm(true);
                        } : undefined}
                        className={`text-center ${compact ? 'p-2 rounded-xl' : 'p-2.5 sm:p-3 rounded-2xl'} metallic-slot relative ${
                            !(item.is_sold > 0) 
                                ? 'cursor-pointer active:scale-95 group/fw' 
                                : 'opacity-60 cursor-not-allowed'
                        }`}
                        title={!(item.is_sold > 0) ? `Forward ${item.name} for $${Number(item.pricefw).toLocaleString()}/day` : undefined}
                    >
                        <div className="flex items-center justify-center gap-1 text-neutral-400 text-[10px] font-semibold uppercase tracking-wider mb-1 group-hover/fw:text-white transition-colors">
                            <ForwardIcon className="w-3 h-3 text-neutral-400 group-hover/fw:text-white shrink-0 group-hover/fw:animate-bounce-x transition-colors" />
                            <span>Fw</span>
                        </div>
                        {item.discount_fw > 0 ? (
                            <div className="flex flex-col items-center">
                                <p 
                                    className={`${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} font-bold font-mono text-white transition-colors`}
                                    title={`$${Number(item.pricefw * (1 - item.discount_fw / 100)).toLocaleString()}/day`}
                                >
                                    ${formatCompactPrice(item.pricefw * (1 - item.discount_fw / 100))}<span className="text-[10px] font-normal text-neutral-400">/day</span>
                                </p>
                                <p 
                                    className="text-[10px] font-mono line-through text-neutral-500"
                                    title={`$${Number(item.pricefw).toLocaleString()}/day`}
                                >
                                    ${formatCompactPrice(item.pricefw)}
                                </p>
                            </div>
                        ) : (
                            <p 
                                className={`${compact ? 'text-xs sm:text-sm' : 'text-sm sm:text-base'} font-bold font-mono text-white transition-colors`}
                                title={`$${Number(item.pricefw).toLocaleString()}/day`}
                            >
                                ${formatCompactPrice(item.pricefw)}<span className="text-[10px] font-normal text-neutral-400">/day</span>
                            </p>
                        )}
                    </div>
                )}
            </div>
        </Link>
        </div>
    );
};
